import { useEffect, useMemo } from "react";
import { Row, Col, Empty, Spin, Divider, Typography } from "antd";
import { useSelector, useDispatch } from "react-redux";
import DocumentCard from "../../../components/CustomCard/DocumentCard";
import { document as fetchDocuments } from "../../../redux/slices/DocumentSlice";
import { DESC_TXT } from "../../../constants/constants";

const { Text, Title } = Typography;

const DEAL_DOC_MASTER_IDS = new Set([
  "DMS1000049", // Risk Disclosure
  "DMS1000050", // Transfer of Rights
]);

const pickLatestByMasterId = (docs) => {
  const map = {};

  docs.forEach((doc) => {
    const key = doc.docMasterId;
    const existing = map[key];

    if (!existing || new Date(doc.modifiedAt) > new Date(existing.modifiedAt)) {
      map[key] = doc;
    }
  });

  return Object.values(map);
};

const Documents = ({ orgId }) => {
  const dispatch = useDispatch();
  const { docs, loading } = useSelector((state) => state.documents);

  useEffect(() => {
    if (!orgId) return;
    dispatch(fetchDocuments({ type: "INVESTOR", orgId }));
  }, [dispatch, orgId]);

  const { otherDocs, dealGroups } = useMemo(() => {
    if (!Array.isArray(docs)) {
      return { otherDocs: [], dealGroups: [] };
    }

    const nonDealDocs = [];
    const dealDocsByDealId = {};

    docs.forEach((doc) => {
      if (DEAL_DOC_MASTER_IDS.has(doc.docMasterId)) {
        const key = doc.dealId || "unknown";
        if (!dealDocsByDealId[key]) dealDocsByDealId[key] = [];
        dealDocsByDealId[key].push(doc);
        return;
      }
      nonDealDocs.push(doc);
    });

    const dealGroups = Object.entries(dealDocsByDealId)
      .map(([dealId, dealDocs]) => ({
        dealId,
        docs: pickLatestByMasterId(dealDocs).sort((a, b) =>
          String(a.docMasterId).localeCompare(String(b.docMasterId)),
        ),
      }))
      .sort((a, b) => {
        if (a.dealId === "unknown") return 1;
        if (b.dealId === "unknown") return -1;
        return String(b.dealId).localeCompare(String(a.dealId));
      });

    return {
      otherDocs: pickLatestByMasterId(nonDealDocs),
      dealGroups,
    };
  }, [docs]);

  const hasDocs = otherDocs.length > 0 || dealGroups.length > 0;

  const renderDocGrid = (docList) => (
    <Row gutter={[16, 16]} style={{ marginTop: 10 }} align="stretch">
      {docList.map((doc) => (
        <Col
          xs={24}
          md={12}
          key={doc.docId || `${doc.docMasterId}-${doc.dealId}`}
        >
          <DocumentCard doc={doc} docType="INVESTOR" orgId={orgId} />
        </Col>
      ))}
    </Row>
  );

  return (
    <Spin spinning={loading}>
      <div style={{ padding: 20 }}>
        {!hasDocs ? (
          <Empty description="No Documents Found" />
        ) : (
          <>
            {otherDocs.length > 0 && <>{renderDocGrid(otherDocs)}</>}

            {otherDocs.length > 0 && dealGroups.length > 0 && <Divider />}

            {dealGroups.map((group, index) => (
              <div
                key={group.dealId}
                style={{ marginTop: index === 0 ? 0 : 24 }}
              >
                <Title level={5} style={{ marginBottom: 4 }}>
                  {group.dealId === "unknown"
                    ? "Deal Documents"
                    : `Deal ${group.dealId}`}
                </Title>
                <Text type="secondary">
                  {DESC_TXT.UPLOAD_DOC_DESC + ".pdf"}
                </Text>
                {renderDocGrid(group.docs)}
                {index < dealGroups.length - 1 && <Divider />}
              </div>
            ))}
          </>
        )}
      </div>
    </Spin>
  );
};

export default Documents;
